import { TFunction } from "i18next";
import {
  ALL_ORDERS_DOWNLOAD,
  INVOICE_CONSOLIDATION,
  ORDERS_BY_LINE_ITEM_DOWNLOAD,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "./constants";
import { PENDING_JOBS_DOWNLOADS_KEY } from "./eventConstants";
import { formatDate, formatTime } from "./formatters";
import LocalStorage from "./LocalStorage";

export interface PendingCompilationJob {
  jobId: string;
  createdAt: number;
  accountId: string;
  type: string;
  fileName?: string;
  displayName?: string;
}

export const getSurplusReturnTarget = (
  returnId: string,
  dealerId: string,
  enableDelegateLogin = false
) => {
  const to = `/returns/surplus/${returnId}`;

  return enableDelegateLogin
    ? { to: `${to}?dealerId=${dealerId}` }
    : { to, state: { dealerAccountId: dealerId } };
};

const updatePendingJobsList = (
  accountId: string,
  newPendingPromotions: PendingCompilationJob[]
): PendingCompilationJob[] => {
  LocalStorage.setJSONValue(PENDING_JOBS_DOWNLOADS_KEY, {
    ...LocalStorage.getJSONValue(PENDING_JOBS_DOWNLOADS_KEY),
    [accountId]: newPendingPromotions,
  });
  return [...newPendingPromotions];
};

export const getPendingDownloadJobs = (
  accountId: string
): PendingCompilationJob[] =>
  (LocalStorage.getJSONValue(PENDING_JOBS_DOWNLOADS_KEY)[
    accountId
  ] as PendingCompilationJob[]) || [];

export const removeExpiredCompilationJobs = (
  accountId: string,
  remainingJobs: PendingCompilationJob[],
  timeout: number
): {
  validJobs: PendingCompilationJob[];
  expiredJobs: PendingCompilationJob[];
} => {
  const { validJobs, expiredJobs } = remainingJobs.reduce(
    (acc, job) => {
      if (Date.now() - job.createdAt >= timeout) {
        return { ...acc, expiredJobs: [...acc.expiredJobs, job] };
      }
      return { ...acc, validJobs: [...acc.validJobs, job] };
    },
    {
      validJobs: [] as PendingCompilationJob[],
      expiredJobs: [] as PendingCompilationJob[],
    }
  );

  updatePendingJobsList(accountId, validJobs);
  return { validJobs, expiredJobs };
};

export const addPendingDownloadJobs = (
  accountId: string,
  newJobs: PendingCompilationJob[]
): PendingCompilationJob[] => {
  const updatedJobs = [...getPendingDownloadJobs(accountId), ...newJobs];
  return updatePendingJobsList(accountId, updatedJobs);
};

export const removeCompletedCompilationJobs = (
  accountId: string,
  completedJobIds: string[]
): PendingCompilationJob[] => {
  const storedPendingDownloads = getPendingDownloadJobs(accountId);
  if (completedJobIds.length === 0) {
    return storedPendingDownloads;
  }
  const updatedPendingDownloads = storedPendingDownloads.filter(
    (job) => !completedJobIds.includes(job.jobId)
  );
  return updatePendingJobsList(accountId, updatedPendingDownloads);
};

export const getDownloadSuccessToastMessage = (
  payloadType: string,
  partialError: string | undefined,
  t: TFunction,
  displayName: string | undefined
): string => {
  if (partialError && payloadType) {
    return partialError;
  }
  const dataMap = getSuccessMessagesMap(t, displayName);
  if (payloadType === "" || payloadType === undefined) return dataMap.promotion;
  return dataMap[payloadType] ?? dataMap.Invoice;
};

const getSuccessMessagesMap = (
  t: TFunction,
  displayName: string | undefined
): { [key: string]: string } => ({
  [ALL_ORDERS_DOWNLOAD]: t("notifications.allOrders.download.success.toast", {
    fileName: displayName || "All Orders",
  }),
  [ORDERS_BY_LINE_ITEM_DOWNLOAD]: t(
    "notifications.orderByLineItem.download.success.toast"
  ),
  Invoice: t("notifications.invoiceFileDownloadSuccessToastMessage", {
    filename: displayName,
  }),
  [RETURNS_EXCEL]: t("notifications.returnsFileDownloadSuccessToastMessage"),
  [RETURN_SUMMARY_EXCEL]: t(
    "notifications.returnsFileDownloadSuccessToastMessage"
  ),
  promotion: t("notifications.promotionFileDownloadSuccessToastMessage", {
    filename: displayName,
  }),
});

export const getDownloadFailureToastMessage = (
  payloadType: string,
  payloadCreatedDate: string,
  t: TFunction,
  displayName: string | undefined,
  language: string
): string => {
  const dataMap = getFailureMessagesMap(
    payloadCreatedDate,
    t,
    language,
    displayName
  );
  return dataMap[payloadType] ?? dataMap.promotion;
};

const getFailureMessagesMap = (
  payloadCreatedDate: string,
  t: TFunction,
  language: string,
  displayName: string | undefined
): { [key: string]: string } => ({
  [ALL_ORDERS_DOWNLOAD]: t("notifications.allOrders.download.failure.message", {
    fileName: displayName || "All Orders",
  }),
  [ORDERS_BY_LINE_ITEM_DOWNLOAD]: t(
    "notifications.orderByLineItem.download.failure.message"
  ),
  [INVOICE_CONSOLIDATION]: t("notifications.consolidateInvoices.failure.v2", {
    date: formatDate(payloadCreatedDate, language),
    time: formatTime(payloadCreatedDate, language),
  }),
  [RETURNS_EXCEL]: t("notifications.returnsExcelDownload.failure"),
  [RETURN_SUMMARY_EXCEL]: t("notifications.returnSummaryDownload.failure"),
  promotion: t("notifications.promotionFileDownloadFailureToastMessage", {
    filename: displayName,
  }),
});
